import { Layout } from 'antd';
import { useContext, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { Redirect, Route, Switch, withRouter } from 'react-router-dom';
import type { Dispatch } from 'redux';
import { useStore } from 'zustand';

import { SideMenuHotel } from 'app/components/layout/SideMenuHotel';
import { inventoryStore } from 'app/components/pages/Extranet/ExtranetInventory/Legacy/InventoryStore';
import {
  ConfigContext,
  ConfigContextProvider,
} from 'app/context/ConfigContext/ConfigContext';
import { ExtranetFeatureAccessContextProvider } from 'app/context/ExtranetFeatureAccess/ExtranetFeatureAccess';
import {
  HotelContext,
  HotelContextProvider,
} from 'app/context/HotelContext/HotelContext';
import { TaskCenterContext } from 'app/context/TaskCenterContext/TaskCenterContext';
import { TaskCenterWrapper } from 'app/context/TaskCenterContext/TaskCenterWrapper';
import {
  UserContext,
  UserContextProvider,
} from 'app/context/UserContext/UserContext';
import { UsersnapProvider } from 'app/context/UsersnapContext/UsersnapContext';
import { useInitializeApp } from 'app/hooks/useInitializeApp';
import { useMoveZendesk } from 'app/hooks/useZendeskWidget/useMoveZendesk';
import { useZendeskWidget } from 'app/hooks/useZendeskWidget/useZendeskWidget';
import { initApp } from 'app/redux/actions/navigation';
import type { RootState } from 'app/redux/store';
import { amplitudeTrack } from 'app/utils/track/amplitude';

import { CurrencyWrapper } from './commons/Currency/CurrencyWrapper/CurrencyWrapper';
import { ExtranetBills } from './pages/Extranet/ExtranetBills/ExtranetBills';
import { BookingsRouter } from './pages/Extranet/ExtranetBookings/BookingsRouter';
import { ExtranetContact } from './pages/Extranet/ExtranetContact/ExtranetContact';
import { ExtranetDashboard } from './pages/Extranet/ExtranetDashboard/ExtranetDashboard';
import { InventoryRouter } from './pages/Extranet/ExtranetInventory/InventoryRouter';
import { inventoryStore as modernInventoryStore } from './pages/Extranet/ExtranetInventory/Modern/InventoryStore';
import { PackagesRouter } from './pages/Extranet/ExtranetPackages/PackagesRouter';
import { ExtranetPayment } from './pages/Extranet/ExtranetPayment/ExtranetPayment';
import { ReviewsRouter } from './pages/Extranet/ExtranetReviews/ReviewsRouter';
import { TaskCenter } from './pages/Extranet/commons/TaskCenter/Legacy/TaskCenter';
import { ModernTaskCenter } from './pages/Extranet/commons/TaskCenter/Modern/ModernTaskCenter';

type HotelAppProps = {
  appReady: boolean;
  init: () => void;
};

export const EXTRANET_INVENTORY_PATH = '/extranet/inventory2';

const HotelAppComponent = ({ init, appReady }: HotelAppProps) => {
  const { user, token } = useContext(UserContext);
  const hotelId = useInitializeApp('extranet', user);

  const config = useContext(ConfigContext);

  useZendeskWidget();

  useEffect(() => {
    init();
  }, [init]);

  if (!user || !appReady || !hotelId) {
    return null;
  }

  if (
    user &&
    user.role === 'user' &&
    (!user.hotelIds || !user.hotelIds.length) &&
    config?.appUrl
  ) {
    return <Redirect to={config.appUrl} />;
  }

  return (
    <ConfigContextProvider>
      <UserContextProvider token={token} user={user}>
        <HotelContextProvider urlHotelId={hotelId}>
          <ExtranetFeatureAccessContextProvider>
            <UsersnapProvider>
              <TaskCenterWrapper>
                <ExtranetRouter />
              </TaskCenterWrapper>
            </UsersnapProvider>
          </ExtranetFeatureAccessContextProvider>
        </HotelContextProvider>
      </UserContextProvider>
    </ConfigContextProvider>
  );
};

const ExtranetRouter = () => {
  const { isTaskCenterOpen, openTask, page } = useContext(TaskCenterContext);

  const { token, user } = useContext(UserContext);
  const config = useContext(ConfigContext);
  const { hotelId, hotelInfo } = useContext(HotelContext);

  const isBulkEditOpen = useStore(
    inventoryStore,
    (state) => state.isBulkEditOpen
  );

  const isBulkExperienceEditDrawerOpen = useStore(
    modernInventoryStore,
    (state) => state.experienceBulkEditDrawer.isOpened
  );

  const isRoomBulkEditDrawerOpen = useStore(
    modernInventoryStore,
    (state) => state.roomBulkEditDrawer.isOpened
  );

  const isExperienceConfigurationDrawerOpen = useStore(
    modernInventoryStore,
    (state) => state.experienceStockConfig?.isOpen
  );

  const isDrawerPackConfigOpen = useStore(
    modernInventoryStore,
    (state) => state.drawerPackConfig?.isOpen
  );

  const isPricingDrawerOpened = useStore(
    modernInventoryStore,
    (state) => state.isPricingDrawerOpened
  );

  useMoveZendesk({
    moveZendesk: true,
    raise: page === 'Contact' ? !isTaskCenterOpen : false,
    drawerOpen:
      isBulkEditOpen ||
      isTaskCenterOpen ||
      isBulkExperienceEditDrawerOpen ||
      isRoomBulkEditDrawerOpen ||
      isExperienceConfigurationDrawerOpen ||
      isDrawerPackConfigOpen ||
      isPricingDrawerOpened,
    forceReset: !!openTask,
    rightOffset: isPricingDrawerOpened ? 800 : undefined,
  });

  return (
    <CurrencyWrapper hotel={hotelInfo}>
      <Helmet>
        <title>Staycation - Extranet</title>
      </Helmet>
      <Layout className="app">
        <SideMenuHotel />
        {hotelInfo?.isLegacy ? <TaskCenter /> : <ModernTaskCenter />}
        {hotelInfo && (
          <Switch>
            <Route
              path="/extranet/inventory"
              render={() => <InventoryRouter key={hotelId} />}
            />
            <Route path="/extranet/dashboard" component={ExtranetDashboard} />
            <Route
              path="/extranet/bookings"
              render={() => <BookingsRouter key={hotelId} />}
            />
            <Route path="/extranet/bills" component={ExtranetBills} />
            <Route
              path="/extranet/reviews"
              render={() => <ReviewsRouter key={hotelId} />}
            />
            <Route
              path="/extranet/packages"
              render={() => <PackagesRouter key={hotelId} />}
            />
            <Route path="/extranet/payment" component={ExtranetPayment} />
            <Route
              path="/extranet/stripe-refresh/:hotelId/"
              component={() => {
                amplitudeTrack('View Page', { page: 'payment' });
                window.location.replace(
                  `${config?.apiUrl}/hotels/${hotelId}/stripe-session?token=${token}`
                );

                return null;
              }}
            />
            <Route path="/extranet/contact" component={ExtranetContact} />
            <Redirect
              to={`/extranet/inventory?hotelId=${user?.hotelIds[0] ?? hotelId}`}
            />
          </Switch>
        )}
      </Layout>
    </CurrencyWrapper>
  );
};

const mapStateToProps = (state: RootState) => ({
  appReady: state.navigation.appReady ?? false,
  hotelId: state.hotelAdmin.hotelId,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  init: () => dispatch(initApp('extranet')),
});

export const HotelApp = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(HotelAppComponent)
);
